import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { fetchPackages } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { DESTINATIONS } from "@/config/siteConfig";
import Hero from "@/components/home/Hero";
import Marquee from "@/components/Marquee";
import { WhyKashmir, HowItWorks, WhatsIncluded, WhyUs } from "@/components/home/HomeSections";
import { HoneymoonSection, FamilySection } from "@/components/home/SegmentSections";
import { Testimonials, InstagramSection, FAQSection, FinalCTA } from "@/components/home/SocialSections";
import USPSection from "@/components/home/USPSection";
import SectionHeading from "@/components/SectionHeading";
import Reveal from "@/components/Reveal";
import PackageCard from "@/components/PackageCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const PackagesSection = ({ packages, isLoading }) => (
  <section className="py-16 sm:py-24 bg-white" data-testid="packages-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="Handcrafted Trips" title="Popular Kashmir Holiday Packages"
        subtitle="Carefully planned Kashmir holidays — every package can be customised around your dates, preferences and budget." />
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-96 rounded-xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {packages.map((pkg) => <PackageCard key={pkg.slug} pkg={pkg} />)}
        </div>
      )}
      <div className="mt-10 text-center">
        <Button asChild variant="outline" className="rounded-full px-6 border-[#0F2F24] text-[#0F2F24] hover:bg-[#0F2F24] hover:text-white">
          <Link to="/kashmir-tour-packages" data-testid="view-all-packages-link">
            View All Kashmir Packages <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  </section>
);

const DestinationsSection = () => (
  <section className="py-16 sm:py-24" id="destinations" data-testid="destinations-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <SectionHeading overline="Where You'll Go" title="Explore Kashmir"
        subtitle="From Dal Lake's houseboats to the remote beauty of Gurez — discover the valleys that make Kashmir unforgettable." />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {DESTINATIONS.map((d, i) => (
          <Reveal key={d.id} delay={i * 0.07}>
            <Link to={d.link} data-testid={`destination-card-${d.id}`}
              className="group relative block aspect-[4/3] overflow-hidden rounded-xl shadow-sm">
            <img src={d.image} alt={`${d.name}, Kashmir`} loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F2F24]/90 via-[#0F2F24]/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5">
              <h3 className="font-serif text-xl font-semibold text-white">{d.name}</h3>
              <p className="mt-1 text-xs sm:text-sm text-white/75">{d.blurb}</p>
            </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const HomePage = () => {
  useSEO({
    title: "Kashmir Holiday Packages | Kashmir Tours & Holidays | Book Kashmir",
    description: "Plan your Kashmir holiday with personalised tour packages covering Srinagar, Gulmarg, Pahalgam and more. Explore Kashmir with comfortable stays, transfers and custom itineraries.",
    path: "/",
  });
  const { data: packages = [], isLoading } = useQuery({ queryKey: ["packages"], queryFn: fetchPackages });

  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <WhyKashmir />
      <PackagesSection packages={packages} isLoading={isLoading} />
      <DestinationsSection />
      <HoneymoonSection />
      <FamilySection />
      <HowItWorks />
      <WhatsIncluded />
      <WhyUs />
      <Testimonials />
      <InstagramSection />
      <FAQSection />
      <USPSection />
      <FinalCTA />
    </main>
  );
};

export default HomePage;
